// Frontend-only guided chatbot for the KPMG Africa CoE platform.
// Handles small talk, fuzzy keyword matching, and a topic tree of canned
// answers grounded in the site's actual content. Escalation path:
// privateenterprise@kpmg.co.ke.

import React, { useState, useEffect, useRef } from "react";
import { FaComments, FaTimes, FaPaperPlane } from "react-icons/fa";
import "../Style/Chatbot.css";

const KPMG_EMAIL = "privateenterprise@kpmg.co.ke";

// --- Small talk -----------------------------------------------------------
const SMALL_TALK = [
  {
    keywords: ["hello", "hi", "hey", "hola", "howdy", "yo", "habari", "jambo"],
    replies: [
      "Hello! 👋 I'm the KPMG Africa CoE Virtual Assistant. How may I help you today?",
      "Hi there! How can I help — pick a topic below or ask a question.",
      "Hey! Welcome to KPMG Africa Centre of Excellence. What can I help you with?",
    ],
    chips: ["about", "register", "programs", "events", "expert"],
  },
  {
    keywords: ["good morning", "good afternoon", "good evening", "good day"],
    replies: [
      "Good day! How can I help you with the KPMG Africa Centre of Excellence?",
    ],
    chips: ["about", "register", "programs", "events", "expert"],
  },
  {
    keywords: [
      "thanks",
      "thank you",
      "thx",
      "ty",
      "appreciate",
      "asante",
      "much appreciated",
    ],
    replies: [
      "You're very welcome. Anything else I can help with?",
      "Glad to help! Is there anything else you'd like to know?",
    ],
    chips: ["about", "register", "programs", "expert"],
  },
  {
    keywords: ["bye", "goodbye", "see you", "cya", "later", "kwaheri"],
    replies: [
      "Goodbye! Feel free to reach out any time, or email " +
        KPMG_EMAIL +
        " to speak with the team.",
    ],
  },
  {
    keywords: ["who are you", "what are you", "your name", "who is this"],
    replies: [
      "I'm the KPMG Africa Centre of Excellence Virtual Assistant — a guide to the platform. I can answer common questions and connect you with our team when needed.",
    ],
    chips: ["about", "register", "expert"],
  },
  {
    keywords: ["how are you", "how's it going", "how are things"],
    replies: [
      "I'm doing well, thank you. More importantly — how can I help you today?",
    ],
    chips: ["about", "register", "programs", "expert"],
  },
  {
    keywords: ["help", "menu", "options", "what can you do", "topics"],
    replies: [
      "I can guide you through the Centre of Excellence. Pick a topic below, or type your question.",
    ],
    chips: ["about", "audiences", "register", "programs", "events", "expert"],
  },
  {
    keywords: ["yes", "yeah", "yep", "sure", "ok", "okay"],
    replies: ["Great. What would you like to explore?"],
    chips: ["about", "register", "programs", "events", "expert"],
  },
  {
    keywords: ["no", "nope", "not really"],
    replies: ["No problem. I'm here whenever you need me."],
    chips: ["about", "register", "expert"],
  },
];

// --- Topic tree -----------------------------------------------------------
const TOPICS = {
  // ---- About / Overview ----
  about: {
    label: "About the Centre",
    keywords: ["about", "centre", "center", "platform", "coe", "kpmg", "what is", "explain", "overview", "purpose", "mission", "vision"],
    reply:
      "The KPMG Africa Centre of Excellence is a Pan-African platform that connects innovators with investors, supports investment-readiness, and shares curated market insight across the continent. It's operated by KPMG as a neutral, professional intermediary.",
    next: ["pillars", "howItWorks", "footprint", "audiences"],
  },
  pillars: {
    label: "What does the Centre do?",
    keywords: ["do", "objective", "pillar", "connect", "strengthen", "inform", "coordinate", "value"],
    reply:
      "The Centre is built around four objectives: Connect (matchmaking between capital seekers and providers); Strengthen (investment-readiness support); Inform (curated insights and intelligence); and Coordinate (a neutral hub for regulators, partners and innovators).",
    next: ["matchmaking", "investmentReady", "knowledge", "audiences"],
  },
  howItWorks: {
    label: "How the platform works",
    keywords: ["how it works", "journey", "process", "step", "use", "flow"],
    reply:
      "The journey is simple: register and choose your role; complete a structured profile; explore curated matches, programs or insights; and engage through events, mentorship and direct introductions. Our team is available throughout.",
    next: ["register", "matchmaking", "programs", "expert"],
  },
  footprint: {
    label: "KPMG's African footprint",
    keywords: ["footprint", "africa", "kenya", "uganda", "tanzania", "country", "location", "office", "where", "presence"],
    reply:
      "KPMG Private Enterprise has a strong presence across Africa, with hubs in Nairobi, Kampala and Dar es Salaam supporting innovators, investors and ecosystem partners. The Centre operates as a Pan-African initiative.",
    next: ["events", "audiences", "expert"],
  },

  // ---- Audiences ----
  audiences: {
    label: "Who is it for?",
    keywords: ["who", "audience", "for"],
    reply:
      "The Centre serves three groups: Innovators & Enterprises; Investors; and Regulators & Ecosystem Partners. Tap any group below to learn more.",
    next: ["forInnovators", "forInvestors", "forPartners", "expert"],
  },
  forInnovators: {
    label: "For innovators & enterprises",
    keywords: ["innovator", "innovators", "startup", "startups", "sme", "entrepreneur", "enterprise", "founder", "venture"],
    reply:
      "If you're a startup, SME, family business or growing venture: get matched with investors aligned to your sector and stage; build investment-ready profiles and data rooms; access cross-border opportunities through Trade Connect; and tap into KPMG-led programs and mentorship.",
    next: ["register", "investmentReady", "programs", "matchmaking"],
  },
  forInvestors: {
    label: "For investors",
    keywords: ["vc", "dfi", "bank", "family office", "angel", "investor", "investors", "fund"],
    reply:
      "If you're an investor (VC, DFI, bank, corporate, family office or angel): explore curated, structured deal flow across African markets; filter by geography, sector, stage, ticket size and impact theme; access market intelligence; and discover co-investment opportunities.",
    next: ["register", "sectors", "matchmaking", "knowledge"],
  },
  forPartners: {
    label: "For regulators & partners",
    keywords: ["regulator", "regulators", "partner", "partners", "donor", "donors", "accelerator", "policymaker", "development"],
    reply:
      "If you're a regulator, development partner, donor, accelerator or policymaker: gain a wide-ranging view of innovators, investors and sectoral trends; onboard cohorts and report on outcomes; access knowledge products and webinars; and use the Centre as a neutral collaboration space.",
    next: ["register", "knowledge", "events", "expert"],
  },

  // ---- Account ----
  register: {
    label: "How do I register?",
    keywords: ["register", "registration", "sign up", "signup", "join", "account", "create", "onboard"],
    reply:
      "Click 'Join Now' on the homepage, or visit the Register page directly. You'll create an account as either an Investor or Investee and complete a structured profile so we can match you with the right opportunities.",
    next: ["profile", "login", "matchmaking", "expert"],
    cta: { type: "link", href: "/register", label: "Go to Register" },
  },
  login: {
    label: "Login or password help",
    keywords: ["login", "log in", "sign in", "password", "reset", "forgot", "locked", "access"],
    reply:
      "Sign in from the Login page. If you've forgotten your password, use the 'Reset password' link on that page. If you're still locked out, our team can help.",
    next: ["register", "profile", "expert"],
    cta: { type: "link", href: "/login", label: "Go to Login" },
  },
  profile: {
    label: "Manage my profile",
    keywords: ["profile", "update", "edit", "details", "pitch", "data room"],
    reply:
      "Once logged in, you can update your profile details, upload pitch materials, manage your data room and keep your information current — this directly improves your matchmaking quality.",
    next: ["matchmaking", "investmentReady", "expert"],
  },

  // ---- Platform features ----
  programs: {
    label: "Programs & training",
    keywords: ["program", "programme", "programs", "programmes", "course", "training", "workshop", "accelerator", "academy", "masterclass", "bootcamp", "learn"],
    reply:
      "We run accelerator programs, mentorship initiatives, speed-pitching events and specialised training — covering Tax Tech, Strategic Management, Governance, Operations, Workforce, Finance and more.",
    next: ["events", "mentorship", "register", "expert"],
    cta: { type: "link", href: "/programs", label: "Browse Programs" },
  },
  matchmaking: {
    label: "How matchmaking works",
    keywords: ["match", "matchmaking", "deal", "deal flow", "funding", "capital", "raise", "fundraise", "ticket size"],
    reply:
      "Once your profile is complete, we match you with investors aligned to your sector, stage, ticket size, geography and impact theme. Investors see curated, structured deal flow filtered to their criteria.",
    next: ["investmentReady", "sectors", "register", "expert"],
  },
  investmentReady: {
    label: "Investment-readiness",
    keywords: ["readiness", "ready", "due diligence", "data room", "pitch", "investor ready"],
    reply:
      "Investment-readiness is about meeting institutional investor expectations: a structured profile, a clear pitch, financial documentation and a credible data room. We support you through this with structured onboarding and KPMG advisory.",
    next: ["profile", "matchmaking", "programs", "expert"],
  },
  pitching: {
    label: "Speed pitching",
    keywords: ["pitch", "pitching", "speed pitch", "presentation"],
    reply:
      "Speed pitching events let founders present to investors and receive instant feedback — a short, structured way to test your story and open conversations with capital providers.",
    next: ["events", "investmentReady", "register"],
  },
  mentorship: {
    label: "Mentorship & expert support",
    keywords: ["mentor", "mentorship", "advisory", "advisor", "guidance", "coaching"],
    reply:
      "Through KPMG mentors and advisers, founders get guidance on strategy, finance, operations and growth — helping you navigate business challenges across the venture life cycle.",
    next: ["programs", "expert"],
  },
  tradeConnect: {
    label: "Trade Connect",
    keywords: ["trade", "trade connect", "cross border", "cross-border", "international", "export", "expansion"],
    reply:
      "Trade Connect helps innovators reach beyond local markets — supporting cross-border partnerships, customers and capital flows across African economies.",
    next: ["forInnovators", "matchmaking", "expert"],
  },
  sectors: {
    label: "Sectors covered",
    keywords: ["sector", "sectors", "industry", "fintech", "agtech", "healthtech", "edtech", "energy", "agriculture", "manufacturing", "logistics"],
    reply:
      "We facilitate connections across multiple sectors — including FinTech, AgTech, HealthTech, EdTech, Renewable Energy, Agriculture, Logistics, Manufacturing and more — bridging promising ventures with investors who back them.",
    next: ["matchmaking", "forInvestors", "knowledge"],
  },
  knowledge: {
    label: "Insights & knowledge library",
    keywords: ["knowledge", "insight", "insights", "report", "research", "library", "thought leadership", "intelligence", "venture pulse", "ceo outlook"],
    reply:
      "Our Insights & Thought Leadership section curates KPMG reports, sector intelligence and market analyses — including Venture Pulse, the CEO Outlook, family business research and more.",
    next: ["events", "sectors", "expert"],
  },
  events: {
    label: "Events & summits",
    keywords: ["event", "events", "summit", "webinar", "venture summit", "calendar", "upcoming", "schedule", "africa accelerated"],
    reply:
      "Our flagship is the 2026 KPMG Africa Private Enterprise Venture Summit — Africa Accelerated. We also run roundtables, pitch evenings and webinars across Nairobi, Kampala, Dar es Salaam and online.",
    next: ["pitching", "register", "expert"],
    cta: {
      type: "link",
      href: "https://kpmg.com/ke/en/insights/2026/05/2026-kpmg-africa-private-enterprise-venture-summit.html",
      label: "View the Summit",
      external: true,
    },
  },

  // ---- Trust & policy ----
  privacy: {
    label: "Privacy & cookies",
    keywords: ["privacy", "cookie", "cookies", "data", "gdpr", "policy", "personal information"],
    reply:
      "Your privacy matters to us. You can read our Privacy Policy from the footer, and you can manage cookies via the consent banner on the homepage.",
    next: ["fees", "expert"],
    cta: { type: "link", href: "/privacy-policy", label: "Read Privacy Policy" },
  },
  fees: {
    label: "Fees & subscription",
    keywords: ["fee", "fees", "cost", "price", "pricing", "subscription", "pay", "charge"],
    reply:
      "Some programs and platform features may have associated fees. For the most up-to-date details — including subscription options for your organisation — please reach out to our team directly.",
    next: ["expert"],
  },
  support: {
    label: "Technical support",
    keywords: ["support", "issue", "bug", "broken", "not working", "error", "problem"],
    reply:
      "If something on the platform isn't working as expected, please describe the issue when you contact our team — including the page you were on and what you were trying to do — so we can resolve it quickly.",
    next: ["expert"],
  },

  // ---- Escalation ----
  expert: {
    label: "Talk to a KPMG expert",
    keywords: ["human", "expert", "agent", "advisor", "person", "email", "contact", "speak", "talk"],
    reply:
      `For direct assistance from a KPMG Private Enterprise specialist, please email ${KPMG_EMAIL}. I can open a pre-filled email for you below.`,
    cta: {
      type: "mailto",
      href: `mailto:${KPMG_EMAIL}?subject=Enquiry%20-%20KPMG%20Africa%20Centre%20of%20Excellence&body=Hello%20KPMG%20Private%20Enterprise%20team%2C%0A%0AI'm%20reaching%20out%20via%20the%20Africa%20Centre%20of%20Excellence%20platform%20regarding%3A%0A%0A%0A%0AKind%20regards%2C`,
      label: `Email ${KPMG_EMAIL}`,
    },
    next: ["about", "register", "programs", "events"],
  },
};

// Curated entry topics shown after the greeting and as the safety net
const INITIAL_TOPICS = [
  "about",
  "audiences",
  "register",
  "programs",
  "events",
  "expert",
];

// Exhaustive topic catalogue (shown when input gets disabled, so users can
// see everything available at a glance)
const ALL_TOPIC_GROUPS = [
  { title: "Get started", ids: ["about", "howItWorks", "register", "login"] },
  {
    title: "Who is it for?",
    ids: ["audiences", "forInnovators", "forInvestors", "forPartners"],
  },
  {
    title: "Platform features",
    ids: ["matchmaking", "investmentReady", "pitching", "mentorship", "tradeConnect", "sectors"],
  },
  {
    title: "Programs & insights",
    ids: ["programs", "events", "knowledge", "footprint"],
  },
  {
    title: "Account & policy",
    ids: ["profile", "privacy", "fees", "support"],
  },
  { title: "Talk to us", ids: ["expert"] },
];

// --- Matching helpers -----------------------------------------------------
function normalize(s) {
  return s.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, " ").replace(/\s+/g, " ").trim();
}

// Levenshtein distance for typo tolerance, capped for performance
function lev(a, b) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  if (Math.abs(al - bl) > 3) return 99; // early bail; we only care about small distances
  const v = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) v[j] = j;
  for (let i = 1; i <= al; i++) {
    let prev = i - 1;
    v[0] = i;
    for (let j = 1; j <= bl; j++) {
      const tmp = v[j];
      v[j] =
        a[i - 1] === b[j - 1]
          ? prev
          : 1 + Math.min(prev, v[j - 1], v[j]);
      prev = tmp;
    }
  }
  return v[bl];
}

// Score how well `text` matches a list of keyword strings
function scoreKeywords(text, keywords) {
  let score = 0;
  for (const kw of keywords) {
    if (!kw) continue;
    if (text.includes(kw)) {
      score += kw.includes(" ") ? 4 : 2; // multi-word matches are stronger signals
      continue;
    }
    // token-level fuzzy match (handles typos like "regiser")
    const tokens = text.split(" ");
    for (const tok of tokens) {
      if (tok.length < 4 || kw.length < 4) continue;
      if (lev(tok, kw) <= 1) {
        score += 1;
        break;
      }
    }
  }
  return score;
}

function matchSmallTalk(text) {
  let best = null;
  let bestScore = 0;
  for (const item of SMALL_TALK) {
    const s = scoreKeywords(text, item.keywords);
    if (s > bestScore) {
      bestScore = s;
      best = item;
    }
  }
  return bestScore >= 2 ? best : null;
}

function matchTopic(text) {
  let bestId = null;
  let bestScore = 0;
  for (const [id, topic] of Object.entries(TOPICS)) {
    const s = scoreKeywords(text, topic.keywords || []);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  return bestScore >= 2 ? bestId : null;
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// --- Component ------------------------------------------------------------
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [thread, setThread] = useState([]);
  // Once the user has typed/clicked anything, lock to topic-only mode so the
  // bot stays helpful inside its known knowledge boundary.
  const [inputLocked, setInputLocked] = useState(false);
  const messagesRef = useRef(null);

  useEffect(() => {
    if (isOpen && thread.length === 0) {
      pushBot(
        "Hello! 👋 I'm the KPMG Africa CoE Virtual Assistant. How may I help you today? Say hi or type your question — I'll then guide you with topics.",
        INITIAL_TOPICS
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [thread]);

  function pushBot(text, chips = [], cta = null) {
    setThread((t) => [...t, { from: "bot", text, chips, cta }]);
  }
  function pushUser(text) {
    setThread((t) => [...t, { from: "user", text }]);
  }

  function lockInputAndShowCatalogue(reason) {
    if (inputLocked) return;
    setInputLocked(true);
    setTimeout(() => {
      setThread((t) => [
        ...t,
        {
          from: "bot",
          text:
            reason ||
            "From here, please pick a topic below — these cover everything I can help with.",
          catalogue: true,
        },
      ]);
    }, 350);
  }

  function handleTopic(id) {
    const topic = TOPICS[id];
    if (!topic) return;
    pushUser(topic.label);
    setTimeout(() => {
      pushBot(topic.reply, topic.next || INITIAL_TOPICS, topic.cta || null);
    }, 300);
    if (!inputLocked) lockInputAndShowCatalogue();
  }

  function handleSend() {
    const raw = input.trim();
    if (!raw) return;
    pushUser(raw);
    setInput("");

    const text = normalize(raw);

    setTimeout(() => {
      const small = matchSmallTalk(text);
      if (small) {
        pushBot(pickRandom(small.replies), small.chips || []);
        lockInputAndShowCatalogue();
        return;
      }

      const id = matchTopic(text);
      if (id) {
        const topic = TOPICS[id];
        pushBot(topic.reply, topic.next || INITIAL_TOPICS, topic.cta || null);
        lockInputAndShowCatalogue();
        return;
      }

      // Unmatched free text — escalate gracefully and lock to topics
      pushBot(
        "I couldn't quite match that, but here's everything I can help with. Pick any topic below, or email a KPMG expert directly.",
        ["expert"]
      );
      lockInputAndShowCatalogue(
        "Browse all topics:"
      );
    }, 380);
  }

  function renderCta(cta) {
    if (!cta) return null;
    const isExternal = cta.external || cta.type === "mailto";
    return (
      <a
        className="chatbot-cta"
        href={cta.href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        {cta.label}
      </a>
    );
  }

  return (
    <div className={`chatbot-container ${isOpen ? "open" : ""}`}>
      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? <FaTimes /> : <FaComments />}
      </button>

      {isOpen && (
        <div className="chatbot-content" role="dialog" aria-label="Virtual assistant">
          <div className="chatbot-header">
            <h3>KPMG Africa CoE Virtual Assistant</h3>
            <p>How may I help you today?</p>
          </div>

          <div className="chatbot-messages" ref={messagesRef}>
            {thread.map((msg, i) => (
              <div key={i} className={`message-row ${msg.from}`}>
                <div className={`message ${msg.from === "bot" ? "bot" : "user"}`}>
                  {msg.text}
                </div>
                {msg.from === "bot" && msg.cta && (
                  <div className="chatbot-cta-row">{renderCta(msg.cta)}</div>
                )}
                {msg.from === "bot" && msg.chips && msg.chips.length > 0 && (
                  <div className="chatbot-chips">
                    {msg.chips.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className="chatbot-chip"
                        onClick={() => handleTopic(id)}
                      >
                        {TOPICS[id]?.label || id}
                      </button>
                    ))}
                  </div>
                )}
                {msg.from === "bot" && msg.catalogue && (
                  <div className="chatbot-catalogue">
                    {ALL_TOPIC_GROUPS.map((g) => (
                      <div key={g.title} className="chatbot-catalogue-group">
                        <div className="chatbot-catalogue-title">{g.title}</div>
                        <div className="chatbot-chips">
                          {g.ids.map((id) => (
                            <button
                              key={id}
                              type="button"
                              className="chatbot-chip"
                              onClick={() => handleTopic(id)}
                            >
                              {TOPICS[id]?.label || id}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {!inputLocked ? (
            <div className="chatbot-input">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Say hi or type your question…"
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                aria-label="Message"
              />
              <button onClick={handleSend} aria-label="Send">
                <FaPaperPlane />
              </button>
            </div>
          ) : (
            <div className="chatbot-input chatbot-input-locked">
              <span>Pick a topic above to continue ↑</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Chatbot;
